import { useId, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  ConnectionSelect,
  CreateConnectionSheet,
} from '@/features/connections';
import type { ExecutionImageView } from '@/lib/api/generated/model';

import { isExecutionImageError } from '../errors';

/**
 * Edit a catalog entry: `label`, `image_reference`, and `connection_id` are
 * all editable. A change to `image_reference`/`connection_id` takes effect
 * for any workflow/step already referencing this image by id on its next
 * run — callers who want the old value preserved should add a new catalog
 * entry instead.
 */
export function EditExecutionImageLabelDialog({
  image,
  open,
  isPending,
  error,
  onSubmit,
  onClearError,
  onCancel,
}: {
  image: ExecutionImageView;
  open: boolean;
  isPending: boolean;
  error: unknown;
  onSubmit: (values: {
    label: string;
    imageReference: string;
    connectionId?: string;
  }) => void;
  onClearError: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(image.label);
  const [imageReference, setImageReference] = useState(image.image_reference);
  const [connectionId, setConnectionId] = useState<string | undefined>(
    image.connection_id ?? undefined,
  );
  const labelId = useId();
  const imageReferenceId = useId();
  const connectionIdId = useId();

  const serverError = isExecutionImageError(error) ? error : undefined;
  const genericError =
    error && !serverError ? 'The request failed. Please try again.' : undefined;
  const formMessage =
    serverError?.field === 'form' ? serverError.message : genericError;
  const connectionIdMessage =
    serverError?.field === 'connectionId' ? serverError.message : undefined;

  const trimmedLabel = label.trim();
  const trimmedImageReference = imageReference.trim();

  const [creatingConnection, setCreatingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);
  const connectionFieldRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (isPending) return;
          if (!next) onCancel();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit execution image</DialogTitle>
            <DialogDescription>
              Changing the image reference or connection affects any
              workflow/step already using this image the next time it runs.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-0.5">
                <span id={connectionIdId} className="text-sm font-medium">
                  Connection
                </span>
              </div>
              <ConnectionSelect
                workspaceId={image.workspace_id}
                providerType="oci_registry"
                value={connectionId}
                onChange={(id) => {
                  setConnectionId(id);
                  if (error) onClearError();
                }}
                disabled={isPending}
                aria-label="Connection"
                ref={connectionFieldRef}
                onCreateRequest={() => {
                  setCreatingConnection(true);
                }}
              />
              {connectionStatus ? (
                <p
                  role="status"
                  aria-live="polite"
                  className="text-muted-foreground text-xs"
                >
                  {connectionStatus}
                </p>
              ) : null}
              {connectionIdMessage ? (
                <p className="text-destructive text-xs">
                  {connectionIdMessage}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-0.5">
                <Label htmlFor={imageReferenceId}>Image reference</Label>
                <RequiredMark />
              </div>
              <Input
                id={imageReferenceId}
                value={imageReference}
                onChange={(event) => {
                  setImageReference(event.target.value);
                  if (error) onClearError();
                }}
                disabled={isPending}
                className="font-mono"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-0.5">
                <Label htmlFor={labelId}>Label</Label>
                <RequiredMark />
              </div>
              <Input
                id={labelId}
                value={label}
                onChange={(event) => {
                  setLabel(event.target.value);
                  if (error) onClearError();
                }}
                disabled={isPending}
                autoFocus
              />
            </div>

            {formMessage ? (
              <p role="alert" className="text-destructive text-sm">
                {formMessage}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                onSubmit({
                  label: trimmedLabel,
                  imageReference: trimmedImageReference,
                  connectionId,
                });
              }}
              disabled={
                isPending || trimmedLabel === '' || trimmedImageReference === ''
              }
            >
              {isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* The registry connection this form needs, made without leaving it
          (spec 124 US1). Beside the dialog, so Esc closes it alone. */}
      <CreateConnectionSheet
        workspaceId={image.workspace_id}
        open={creatingConnection}
        providerType="oci_registry"
        finalFocus={connectionFieldRef}
        onCreated={(connection) => {
          if (connection.enabled) {
            setConnectionId(connection.id);
            setConnectionStatus(`Connection ${connection.name} added`);
          } else {
            setConnectionStatus(
              `Connection ${connection.name} was saved disabled — enable it on the Connections page to use it here`,
            );
          }
        }}
        onClose={() => {
          setCreatingConnection(false);
        }}
      />
    </>
  );
}
